import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer'

// Define interfaces for the data structures
interface User {
  id: number
  first_name: string
  last_name: string
  email: string
  phone?: string
}

interface Customer {
  id: number
  name: string
  email?: string
  phone?: string
}

interface Person {
  id: number
  name: string
  email?: string
  phone?: string
  is_primary: boolean
}

interface Deal {
  id: number
  title?: string
  name?: string
  amount: string
  customer_name: string
  customer_email?: string
  customer_phone?: string
  type_name?: string
  base_points: string
  bonus_points: string
  volume_points: string
  closing_date?: string
  expected_close_date?: string
  status_name: string
  description?: string
  created_at: string
  updated_at: string
}

interface DealHistory {
  id: number
  action_type: string
  comment?: string
  comment_type?: string
  created_at: string
  created_by_username?: string
  first_name?: string
  last_name?: string
}

interface SMSMessage {
  id: number
  content: string
  direction: 'inbound' | 'outbound'
  from_phone: string
  to_phone: string
  created_at: string
}

interface DealPDFProps {
  deal: Deal
  user: User
  customer: Customer
  persons: Person[]
  history: DealHistory[]
  smsMessages: SMSMessage[]
  language?: 'en' | 'fr'
}

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica'
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    color: '#2563eb'
  },
  section: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    border: '1px solid #e2e8f0'
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1e40af',
    borderBottom: '1px solid #ddd',
    paddingBottom: 5
  },
  row: {
    flexDirection: 'row',
    marginBottom: 8
  },
  label: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
    width: 120,
    minWidth: 120
  },
  value: {
    fontSize: 11,
    color: '#111827',
    flex: 1
  },
  historyItem: {
    marginBottom: 12,
    padding: 8,
    backgroundColor: '#ffffff',
    borderRadius: 3,
    border: '1px solid #e5e7eb'
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5
  },
  historyType: {
    fontSize: 9,
    fontWeight: 'bold',
    backgroundColor: '#e0f2fe',
    color: '#0277bd',
    padding: 3,
    borderRadius: 2
  },
  historyDate: {
    fontSize: 9,
    color: '#6b7280'
  },
  historyUser: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 3
  },
  historyComment: {
    fontSize: 10,
    color: '#111827',
    lineHeight: 1.4
  },
  smsContainer: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#ffffff',
    borderRadius: 3,
    border: '1px solid #e5e7eb'
  },
  smsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4
  },
  smsDirection: {
    fontSize: 9,
    fontWeight: 'bold',
    padding: 2,
    borderRadius: 2
  },
  smsInbound: {
    backgroundColor: '#dcfce7',
    color: '#15803d'
  },
  smsOutbound: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8'
  },
  smsDate: {
    fontSize: 9,
    color: '#6b7280'
  },
  smsContent: {
    fontSize: 10,
    color: '#111827',
    lineHeight: 1.4
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#6b7280'
  },
  footer: {
    position: 'absolute',
    bottom: 50,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    color: '#9ca3af',
    borderTop: '1px solid #e5e7eb',
    paddingTop: 10
  }
})

const translations = {
  en: {
    dealReport: 'Deal Compliance Report',
    brokerInfo: 'Mortgage Broker Information',
    customerInfo: 'Customer Information',
    dealDetails: 'Deal Details',
    historyComments: 'Comments',
    smsConversations: 'SMS Conversations',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    dealName: 'Deal Name',
    dealType: 'Deal Type',
    loanAmount: 'Loan Amount',
    basePoints: 'Base Points',
    bonusPoints: 'Bonus Points',
    volumePoints: 'Volume Points',
    expectedCloseDate: 'Expected Close Date',
    status: 'Status',
    description: 'Description',
    relatedPersons: 'Related Persons',
    primary: 'Primary',
    secondary: 'Secondary',
    inbound: 'Received',
    outbound: 'Sent',
    noSmsMessages: 'No SMS conversations found for this customer.',
    generatedAt: 'Generated at',
    page: 'Page'
  },
  fr: {
    dealReport: 'Rapport de Conformité du Dossier',
    brokerInfo: 'Informations du Courtier Hypothécaire',
    customerInfo: 'Informations du Client',
    dealDetails: 'Détails du Dossier',
    historyComments: 'Commentaires',
    smsConversations: 'Conversations SMS',
    name: 'Nom',
    email: 'Courriel',
    phone: 'Téléphone',
    dealName: 'Nom du Dossier',
    dealType: 'Type de Dossier',
    loanAmount: 'Montant du Prêt',
    basePoints: 'Points de Base',
    bonusPoints: 'Points Bonus',
    volumePoints: 'Points Volume',
    expectedCloseDate: 'Date de Fermeture Prévue',
    status: 'Statut',
    description: 'Description',
    relatedPersons: 'Personnes Associées',
    primary: 'Principal',
    secondary: 'Secondaire',
    inbound: 'Reçu',
    outbound: 'Envoyé',
    noSmsMessages: 'Aucune conversation SMS trouvée pour ce client.',
    generatedAt: 'Généré le',
    page: 'Page'
  }
}

const DealPDF: React.FC<DealPDFProps> = ({ 
  deal, 
  user, 
  customer, 
  persons, 
  history, 
  smsMessages, 
  language = 'en' 
}) => {
  const t = translations[language]

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (amount: string | number) => {
    // Handle edge cases where amount might be an object or invalid value
    if (amount === null || amount === undefined || amount === '') {
      return '$0'
    }
    
    // If it's an object, try to extract a numeric value or default to 0
    if (typeof amount === 'object') {
      return '$0'
    }
    
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    
    // Check if the parsed value is a valid number
    if (isNaN(num)) {
      return '$0'
    }
    
    return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-US', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num || 0)
  }

  const getCommentTypeDisplay = (type: string) => {
    switch(type) {
      case 'internal_note': return language === 'fr' ? 'Note Interne' : 'Internal Note'
      case 'call': return language === 'fr' ? 'Appel' : 'Call'
      case 'text': return 'SMS'
      case 'email': return language === 'fr' ? 'Courriel' : 'Email'
      case 'in_person': return language === 'fr' ? 'En Personne' : 'In-Person'
      case 'other': return language === 'fr' ? 'Autre' : 'Other'
      default: return language === 'fr' ? 'Commentaire' : 'Comment'
    }
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <Text style={styles.title}>{t.dealReport}</Text>
        
        {/* Broker Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t.brokerInfo}</Text>
          <View style={styles.row}>
            <Text style={styles.label}>{t.name}:</Text>
            <Text style={styles.value}>{user.first_name} {user.last_name}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>{t.email}:</Text>
            <Text style={styles.value}>{user.email}</Text>
          </View>
          {user.phone && (
            <View style={styles.row}>
              <Text style={styles.label}>{t.phone}:</Text>
              <Text style={styles.value}>{user.phone}</Text>
            </View>
          )}
        </View>

        {/* Customer Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t.customerInfo}</Text>
          <View style={styles.row}>
            <Text style={styles.label}>{t.name}:</Text>
            <Text style={styles.value}>{customer.name}</Text>
          </View>
          {customer.email && (
            <View style={styles.row}>
              <Text style={styles.label}>{t.email}:</Text>
              <Text style={styles.value}>{customer.email}</Text>
            </View>
          )}
          {customer.phone && (
            <View style={styles.row}>
              <Text style={styles.label}>{t.phone}:</Text>
              <Text style={styles.value}>{customer.phone}</Text>
            </View>
          )}

          {/* Related Persons */}
          {persons && persons.length > 0 && (
            <View style={{ marginTop: 10 }}>
              <Text style={[styles.label, { marginBottom: 5 }]}>{t.relatedPersons}:</Text>
              {persons.map((person, index) => (
                <View key={person.id} style={{ marginLeft: 10, marginBottom: 5 }}>
                  <Text style={[styles.value, { fontWeight: 'bold' }]}>
                    {person.name} ({person.is_primary ? t.primary : t.secondary})
                  </Text>
                  {person.email && (
                    <Text style={styles.value}>  {t.email}: {person.email}</Text>
                  )}
                  {person.phone && (
                    <Text style={styles.value}>  {t.phone}: {person.phone}</Text>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Deal Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t.dealDetails}</Text>
          <View style={styles.row}>
            <Text style={styles.label}>{t.dealName}:</Text>
            <Text style={styles.value}>{deal.title || deal.name}</Text>
          </View>
          {deal.type_name && (
            <View style={styles.row}>
              <Text style={styles.label}>{t.dealType}:</Text>
              <Text style={styles.value}>{deal.type_name}</Text>
            </View>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>{t.loanAmount}:</Text>
            <Text style={styles.value}>{formatCurrency(deal.amount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>{t.basePoints}:</Text>
            <Text style={styles.value}>{deal.base_points || '0'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>{t.bonusPoints}:</Text>
            <Text style={styles.value}>{deal.bonus_points || '0'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>{t.volumePoints}:</Text>
            <Text style={styles.value}>{deal.volume_points || '0'}</Text>
          </View>
          {deal.expected_close_date && (
            <View style={styles.row}>
              <Text style={styles.label}>{t.expectedCloseDate}:</Text>
              <Text style={styles.value}>{new Date(deal.expected_close_date).toLocaleDateString()}</Text>
            </View>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>{t.status}:</Text>
            <Text style={styles.value}>{deal.status_name}</Text>
          </View>
          {deal.description && (
            <View style={styles.row}>
              <Text style={styles.label}>{t.description}:</Text>
              <Text style={styles.value}>{deal.description}</Text>
            </View>
          )}
        </View>

        {/* Page Footer */}
        <Text style={styles.footer}>
          {t.generatedAt} {new Date().toLocaleDateString()} - MortgagePipe.ca
        </Text>
        
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${t.page} ${pageNumber} / ${totalPages}`
        )} fixed />
      </Page>

      {/* History & Comments - New Page */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{t.historyComments}</Text>
        
        <View style={styles.section}>
          {history && history.length > 0 ? (
            history.map((entry) => (
              <View key={entry.id} style={styles.historyItem}>
                <View style={styles.historyHeader}>
                  <Text style={styles.historyType}>
                    {getCommentTypeDisplay(entry.comment_type || 'comment')}
                  </Text>
                  <Text style={styles.historyDate}>{formatDate(entry.created_at)}</Text>
                </View>
                
                <Text style={styles.historyUser}>
                  {entry.first_name && entry.last_name 
                    ? `${entry.first_name} ${entry.last_name}`
                    : entry.created_by_username ? 
                      entry.created_by_username.split('@')[0].replace(/\./g, ' ').split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                      ).join(' ') : 
                      'Unknown User'
                  }
                </Text>
                
                {entry.comment && (
                  <Text style={styles.historyComment}>{entry.comment}</Text>
                )}
              </View>
            ))
          ) : (
            <Text style={styles.value}>{language === 'fr' ? 'Aucun commentaire.' : 'No comments available.'}</Text>
          )}
        </View>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${t.page} ${pageNumber} / ${totalPages}`
        )} fixed />
      </Page>

      {/* SMS Conversations - New Page */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{t.smsConversations}</Text>
        
        <View style={styles.section}>
          {smsMessages && smsMessages.length > 0 ? (
            smsMessages.map((message) => (
              <View key={message.id} style={styles.smsContainer}>
                <View style={styles.smsHeader}>
                  <Text style={[
                    styles.smsDirection,
                    message.direction === 'inbound' ? styles.smsInbound : styles.smsOutbound
                  ]}>
                    {message.direction === 'inbound' ? t.inbound : t.outbound}
                  </Text>
                  <Text style={styles.smsDate}>{formatDate(message.created_at)}</Text>
                </View>
                
                <Text style={styles.smsContent}>{message.content}</Text>
                
                <Text style={[styles.smsDate, { marginTop: 4 }]}>
                  {message.direction === 'inbound' ? `From: ${message.from_phone}` : `To: ${message.to_phone}`}
                </Text>
              </View>
            ))
          ) : (
            <Text style={styles.value}>{t.noSmsMessages}</Text>
          )}
        </View>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${t.page} ${pageNumber} / ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  )
}

export default DealPDF 